import { Room } from "../../entities/room.entity";
import { displayDate, getDuration } from "src/utils/dateHandler.util";
import { Badge } from "src/components/Badge/Badge";
import { formatScreamingSnakeCase, languageMap } from "src/utils/util";
import { MenuActions } from "src/components/menu-actions";
import { useLocation } from "react-router-dom";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type Props = {
  data: Room[];
};

const statusStyles: Record<string, string> = {
  INITIATED: "bg-yellow-100 text-yellow-800",
  SCHEDULED: "bg-blue-100 text-blue-800",
  ACTIVE: "bg-green-100 text-green-800",
  ENDED: "bg-gray-100 text-gray-800",
  CANCELLED: "bg-red-100 text-red-800",
  EXPIRED: "bg-orange-100 text-orange-800",
};

const categoryStyles: Record<string, string> = {
  CLASSROOM: "bg-indigo-100 text-indigo-800",
  COMMUNITY: "bg-purple-100 text-purple-800",
};

const privacyStyles: Record<string, string> = {
  PUBLIC: "bg-green-100 text-green-800",
  FOLLOWINGS: "bg-blue-100 text-blue-800",
  PRIVATE: "bg-gray-100 text-gray-800",
};

export const RoomsTable = ({ data }: Props) => {
  const { search } = useLocation();

  return (
    <div>
      <div className="overflow-x-auto mb-6">
        <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
          <thead className="bg-gray-800 text-white text-center">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Language</th>
              <th className="px-4 py-3">Start Time</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Privacy</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">View</th>
            </tr>
          </thead>

          <tbody>
            {data.map((room) => (
              <tr key={room.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-3 border-t border-gray-200 max-w-xs whitespace-nowrap overflow-hidden text-ellipsis">
                  {room.title || "Untitled Room"}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  {languageMap[room.langCode] || room.langCode}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  {displayDate(room.startTime) ?? "__"}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  {getDuration(room.startTime, room.endTime)}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <Badge
                    label={formatScreamingSnakeCase(room.category)}
                    classes={categoryStyles[room.category]}
                  />
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <Badge
                    label={formatScreamingSnakeCase(room.privacy)}
                    classes={privacyStyles[room.privacy]}
                  />
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <Badge
                    label={formatScreamingSnakeCase(room.status)}
                    classes={statusStyles[room.status]}
                  />
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <MenuActions actions={[{ type: "LINK", to: appendSearchToPath(`/rooms/${room.id}`, search), label: "view" }]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.length === 0 && <p className="px-4 py-3 text-center text-gray-500">No Rooms Found</p>}
    </div>
  );
};
